import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import cx from 'classnames'
import { Activity, Children, type ReactNode } from 'react'

import { borderStyles } from '../../libs'
import { typographyStyles } from '../typography/styles'

const SELECTED_TAB_FALLBACK_INDEX = 0

export type TabItem = {
  /**
   * The key of the tab
   */
  key: string
  /**
   * The title of the tab
   */
  title: string
  /**
   * Whether the tab should be disabled
   */
  disabled?: boolean
}

export type TabType = {
  /**
   * The tab items. Use the TabItemtype if you want configure the tab, otherwise a string is fine.
   */
  items: (string | TabItem)[]
  /**
   * The zero based index of the selected tab. Note that this index skips the disabled tabs as you cannot default show something disabled.
   */
  selectedIndex: number
  /**
   * The contents of the tab panel. The contents related to the different tabs must be direct children
   * of the <Tabs> element. Tab 0 wil display child 0, and so on.
   */
  children?: ReactNode
  /**
   * Change callback for notifying parent components when the selected tab index changes.
   */
  onChange: (activeIndex: number) => void
  renderTabMenuComponent?: ReactNode
  /**
   * True if non active tabs should stay mounted, preserving their state. Inactive tabs are rendered inside a hidden
   * `<Activity>`, so their effects are cleaned up while hidden and re-run when the tab becomes visible again (e.g.
   * queries with `refetchOnMount` refetch when the tab is shown).
   */
  shouldMountInactive?: boolean
  /**
   * True if the tab bar should still be rendered when no children are given.
   * Use this when the tab contents are rendered outside of the tab component.
   */
  shouldRenderEmptyChildren?: boolean
  tabItemsClassName?: string
  panelClassName?: string
  tabGroupClassName?: string
}

const toTabItem = (t: string | TabItem) =>
  typeof t === 'string' ? { key: t, title: t } : t

/**
 * If you have too much content, that will not fit on screen. A tab component can divide the content into section
 * and only show one section at a time.
 */
export const Tabs = ({
  items,
  selectedIndex,
  children,
  shouldMountInactive = false,
  shouldRenderEmptyChildren = false,
  tabItemsClassName,
  onChange,
  panelClassName,
  tabGroupClassName,
  renderTabMenuComponent,
}: TabType) => {
  const isSelectedIndexOutOfBounds =
    selectedIndex > items.length - 1 || selectedIndex < 0
  const validatedSelectedIndex = isSelectedIndexOutOfBounds
    ? SELECTED_TAB_FALLBACK_INDEX
    : selectedIndex

  const hasChildren = Children.count(children) > 0
  const hasNoItems = items.length === 0

  return hasNoItems || (!hasChildren && !shouldRenderEmptyChildren) ? null : (
    /**
     * We're using this as a controlled component (https://headlessui.com/react/tabs#controlling-the-active-tab) given that
     * we've experienced issues (Max update depth exceeded errors) mounting Lazily loaded components inside uncontrolled tab components.
     *
     * Hence, the selected index and on-change callbacks are required and can't be undefined.
     */
    <TabGroup
      selectedIndex={validatedSelectedIndex}
      onChange={onChange}
      className={tabGroupClassName}
    >
      <TabList
        className={cx(
          '-mb-px flex space-x-4 overflow-x-auto shrink-0',
          borderStyles,
          'border-b',
          tabItemsClassName,
        )}
      >
        {items &&
          items.map(toTabItem).map(({ key, disabled, title }) => (
            <Tab
              key={key}
              className={cx(typographyStyles())}
              disabled={disabled}
            >
              {({ selected }) => (
                <div
                  className={cx(
                    'whitespace-nowrap border-b-2 px-1 py-2 font-medium text-primary transition-colors rounded-t-lg hover:bg-current/10 hover:border-primary',
                    selected ? 'border-primary' : 'border-transparent',
                  )}
                >
                  {title}
                </div>
              )}
            </Tab>
          ))}
        {renderTabMenuComponent}
      </TabList>
      {hasChildren && (
        <TabPanels className={cx('mt-2', panelClassName)}>
          {({ selectedIndex }) => (
            <>
              {Children.toArray(children).map((child, index) => (
                <TabPanel
                  // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
                  key={index}
                  className={cx(panelClassName, {
                    hidden: selectedIndex !== index,
                  })}
                  static
                >
                  {/* TabPanel created as a static controlled component to fix react recursive rendering bug: jira issue: LIFE-4840  */}
                  {shouldMountInactive ? (
                    <Activity
                      mode={selectedIndex === index ? 'visible' : 'hidden'}
                    >
                      {child}
                    </Activity>
                  ) : (
                    selectedIndex === index && child
                  )}
                </TabPanel>
              ))}
            </>
          )}
        </TabPanels>
      )}
    </TabGroup>
  )
}
